import { getAccessToken, isTestAuthActive, useAuthStore } from '../stores/authStore';

const AUTH_BASE = '/auth';

type RequestOptions = {
  method?: string;
  body?: unknown;
};

async function authFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const token = getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'include',
  };
  if (opts.body != null) init.body = JSON.stringify(opts.body);

  const res = await fetch(`${AUTH_BASE}${path}`, init);

  // Try token refresh on 401
  if (res.status === 401 && token) {
    const refreshRes = await fetch(`${AUTH_BASE}/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (refreshRes.ok) {
      const { accessToken } = (await refreshRes.json()) as { accessToken: string };
      useAuthStore.getState().setAccessToken(accessToken);
      // Retry original request with new token
      const retryInit: RequestInit = {
        method: opts.method ?? 'GET',
        headers: { ...headers, Authorization: `Bearer ${accessToken}` },
        credentials: 'include',
      };
      if (opts.body != null) retryInit.body = JSON.stringify(opts.body);

      const retryRes = await fetch(`${AUTH_BASE}${path}`, retryInit);
      if (!retryRes.ok) throw new ApiError(retryRes.status, await retryRes.text());
      return retryRes.json() as Promise<T>;
    }
    // Refresh failed — force logout and clear the refresh cookie server-side.
    logoutAuth();
  }

  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    body: string,
  ) {
    let msg: string;
    try {
      msg = (JSON.parse(body) as { error?: string }).error ?? body;
    } catch {
      msg = body;
    }
    super(msg);
    this.name = 'ApiError';
  }
}

export function logoutAuth(): void {
  // Dev/E2E bypass — keep the test user signed in even when the upstream
  // auth-service says 401. Production strips this branch.
  if (import.meta.env.DEV && isTestAuthActive()) return;

  // Clear local auth state SYNCHRONOUSLY so the UI flips to logged-out
  // immediately — subscribers re-render this tick, no manual refresh needed.
  // The server cookie-clear is best-effort and runs in the background; the
  // local session must never wait on a network round-trip.
  useAuthStore.getState().logout();

  void fetch(`${AUTH_BASE}/logout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  }).catch(() => {
    // Best-effort only: the local session is already cleared.
  });
}

// ── API Key types ──────────────────────────────────────────────

export type PermissionStatus = 'allowed' | 'denied' | 'unknown';

export type ScopeResult = {
  scope: string;
  label: string;
  status: PermissionStatus;
};

export type ApiKeyPublic = {
  id: string;
  label: string;
  exchange: string;
  apiKeyMasked: string;
  permissions: string[];
  /** Per-scope probe results. Empty array for keys added before issue 005. */
  permissionDetails: ScopeResult[];
  createdAt: string;
};

export type CreateApiKeyRequest = {
  label: string;
  exchange: string;
  apiKey: string;
  apiSecret: string;
};

// ── Portfolio/Wallet types ─────────────────────────────────────

export type WalletType = 'paper' | 'live';

export type WalletConfig = {
  riskPerTradePct: number;
  maxPositionsPerSymbol: number;
  maxTotalPositions: number;
  slAtrMultiplier: number;
  tpAtrMultiplier: number;
  buyThreshold: number;
  sellThreshold: number;
};

export type WalletStats = {
  totalTrades: number;
  winRate: number;
  totalReturnPct: number;
  maxDrawdownPct: number;
};

export type Wallet = {
  _id: string;
  name: string;
  type: WalletType;
  apiKeyId?: string;
  strategySymbols?: Record<string, string[]>;
  startingBalance: number;
  currentBalance: number;
  strategies: string[];
  config: WalletConfig;
  stats: WalletStats;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type CreateWalletRequest = {
  name: string;
  type: WalletType;
  startingBalance?: number | undefined;
  strategies?: string[] | undefined;
  apiKeyId?: string | undefined;
  config?: Partial<WalletConfig> | undefined;
};

// ── API Key endpoints ──────────────────────────────────────────

export function fetchApiKeys(): Promise<ApiKeyPublic[]> {
  return authFetch('/user/api-keys');
}

export function createApiKey(data: CreateApiKeyRequest): Promise<ApiKeyPublic> {
  return authFetch('/user/api-keys', { method: 'POST', body: data });
}

export function deleteApiKey(id: string): Promise<{ deleted: boolean }> {
  return authFetch(`/user/api-keys/${id}`, { method: 'DELETE' });
}

// ── Favourites ─────────────────────────────────────────────────

export function fetchFavourites(): Promise<{ favourites: string[] }> {
  return authFetch('/user/favourites');
}

export function setFavouritesRemote(
  favourites: string[],
): Promise<{ favourites: string[] }> {
  return authFetch('/user/favourites', { method: 'PUT', body: { favourites } });
}

/**
 * Merge client-provided favourites into the server list (union + dedupe).
 * Call after login/register with the localStorage favourites of the anon session.
 */
export function mergeFavouritesRemote(
  favourites: string[],
): Promise<{ favourites: string[] }> {
  return authFetch('/user/favourites/merge', { method: 'POST', body: { favourites } });
}

// ── Wallet/Portfolio endpoints ─────────────────────────────────

export function fetchWallets(): Promise<Wallet[]> {
  return authFetch('/user/portfolios');
}

export function createWallet(data: CreateWalletRequest): Promise<Wallet> {
  return authFetch('/user/portfolios', { method: 'POST', body: data });
}

export function deleteWallet(id: string): Promise<{ deleted: boolean }> {
  return authFetch(`/user/portfolios/${id}`, { method: 'DELETE' });
}

// ── Kraken Live Data ───────────────────────────────────────────

export type KrakenAsset = {
  asset: string;
  amount: string;
  usdPrice: number;
  usdValue: number;
  cadValue: number;
};

export type KrakenBalanceResponse = {
  assets: KrakenAsset[];
  totalUsd: number;
  totalCad: number;
  usdCadRate: number;
  equity: string | null;
  tradeBalance: string | null;
  marginUsed: string | null;
  freeMargin: string | null;
  unrealizedPnl: string | null;
};

export type KrakenOrder = {
  id: string;
  pair: string;
  type: string;
  orderType: string;
  price: string;
  volume: string;
  volumeExecuted: string;
  cost: string;
  fee: string;
  description: string;
  openedAt: number;
};

export type KrakenOrdersResponse = {
  orders: KrakenOrder[];
};

export function fetchKrakenBalance(apiKeyId: string): Promise<KrakenBalanceResponse> {
  return authFetch(`/user/api-keys/${apiKeyId}/balance`);
}

export function fetchKrakenOrders(apiKeyId: string): Promise<KrakenOrdersResponse> {
  return authFetch(`/user/api-keys/${apiKeyId}/orders`);
}

// ── Kraken Positions ───────────────────────────────────────────

export type KrakenPositionEntry = {
  id: string;
  pair: string;
  type: string;
  orderType: string;
  volume: string;
  volumeClosed: string;
  cost: string;
  fee: string;
  margin: string;
  value: string | null;
  unrealizedPnl: string | null;
  openedAt: number;
};

export type KrakenPositionsResponse = {
  positions: KrakenPositionEntry[];
};

export function fetchKrakenPositions(apiKeyId: string): Promise<KrakenPositionsResponse> {
  return authFetch(`/user/api-keys/${apiKeyId}/positions`);
}

// ── Kraken Trade History ───────────────────────────────────────

export type KrakenTradeEntry = {
  id: string;
  pair: string;
  type: string;
  orderType: string;
  price: string;
  cost: string;
  fee: string;
  volume: string;
  margin: string;
  time: number;
  misc: string;
};

export type KrakenTradesResponse = {
  trades: KrakenTradeEntry[];
  count: number;
};

export function fetchKrakenTrades(apiKeyId: string): Promise<KrakenTradesResponse> {
  return authFetch(`/user/api-keys/${apiKeyId}/trades`);
}

// ── Wallet Strategy Assignment ─────────────────────────────────

export function updateWalletStrategies(
  walletId: string,
  strategies: string[],
): Promise<{ updated: boolean; strategies: string[] }> {
  return authFetch(`/user/portfolios/${walletId}/strategies`, {
    method: 'PATCH',
    body: { strategies },
  });
}

export function updateWalletStrategySymbols(
  walletId: string,
  strategySymbols: Record<string, string[]>,
): Promise<{ updated: boolean }> {
  return authFetch(`/user/portfolios/${walletId}/strategy-symbols`, {
    method: 'PATCH',
    body: { strategySymbols },
  });
}

export function updateWalletApiKey(
  walletId: string,
  apiKeyId: string,
): Promise<{ updated: boolean; apiKeyId: string }> {
  return authFetch(`/user/portfolios/${walletId}/api-key`, {
    method: 'PATCH',
    body: { apiKeyId },
  });
}
